import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

const BRAND      = rgb(0.012, 0.173, 0.396); // #032C65
const GRAY       = rgb(0.4, 0.4, 0.4);
const BLACK      = rgb(0, 0, 0);
const DIVIDER    = rgb(0.85, 0.85, 0.85);

const CHURCH = {
  name:    'CHURCH OF THE HOLY SPIRIT',
  address: '1940 University Avenue, Bronx NY 10453',
  phone:   '718-583-0120',
};

export async function generateRegistrationPdf(r) {
  const pdfDoc = await PDFDocument.create();
  const page   = pdfDoc.addPage([612, 792]); // US Letter
  const { width, height } = page.getSize();

  const bold    = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const regular = await pdfDoc.embedFont(StandardFonts.Helvetica);

  // Embed logo (graceful fallback if unavailable)
  let logo = null;
  try {
    const bytes = await fetch('/favicon/favicon.png').then((r) => r.arrayBuffer());
    logo = await pdfDoc.embedPng(bytes);
  } catch { /* skip logo */ }

  const M = 50; // margin
  let y = height - M;

  // ── Header ──────────────────────────────────────────────
  if (logo) {
    const dims = logo.scaleToFit(38, 38);
    page.drawImage(logo, { x: M, y: y - dims.height + 10, width: dims.width, height: dims.height });
  }
  const textX = logo ? M + 48 : M;

  page.drawText(CHURCH.name, { x: textX, y, size: 13, font: bold, color: BRAND });
  y -= 17;
  page.drawText('FAMILY REGISTRATION INFORMATION SHEET', { x: textX, y, size: 10, font: regular, color: GRAY });
  y -= 13;

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  page.drawText(`Generated: ${today}`, { x: textX, y, size: 8, font: regular, color: GRAY });
  y -= 18;

  page.drawLine({ start: { x: M, y }, end: { x: width - M, y }, thickness: 1.5, color: BRAND });
  y -= 22;

  // ── Section renderer ─────────────────────────────────────
  const LABEL_W = 155;
  const VALUE_X = M + LABEL_W;
  const VALUE_MAX = width - M - LABEL_W;

  function drawSection(title, rows) {
    if (y < 80) return;

    page.drawText(title.toUpperCase(), { x: M, y, size: 9.5, font: bold, color: BRAND });
    y -= 5;
    page.drawLine({ start: { x: M, y }, end: { x: width - M, y }, thickness: 0.4, color: DIVIDER });
    y -= 13;

    rows.forEach(({ label, value }) => {
      if (value === undefined || value === null || value === '') return;
      const val = String(value);

      page.drawText(`${label}:`, { x: M, y, size: 9, font: bold, color: GRAY });

      const chars = val.length;
      const approxLineLen = 70;
      if (chars <= approxLineLen) {
        page.drawText(val, { x: VALUE_X, y, size: 9, font: regular, color: BLACK, maxWidth: VALUE_MAX });
        y -= 14;
      } else {
        const split = val.lastIndexOf(' ', approxLineLen);
        const line1 = split > 0 ? val.slice(0, split) : val.slice(0, approxLineLen);
        const line2 = split > 0 ? val.slice(split + 1) : val.slice(approxLineLen);
        page.drawText(line1, { x: VALUE_X, y, size: 9, font: regular, color: BLACK });
        y -= 12;
        page.drawText(line2, { x: VALUE_X, y, size: 9, font: regular, color: BLACK, maxWidth: VALUE_MAX });
        y -= 14;
      }
    });

    y -= 14; // gap after section
  }

  function drawListSection(title, items, keys) {
    if (y < 80) return;

    page.drawText(title.toUpperCase(), { x: M, y, size: 9.5, font: bold, color: BRAND });
    y -= 5;
    page.drawLine({ start: { x: M, y }, end: { x: width - M, y }, thickness: 0.4, color: DIVIDER });
    y -= 13;

    if (!items || items.length === 0) {
      page.drawText('None', { x: M, y, size: 9, font: regular, color: GRAY });
      y -= 14;
    } else {
      items.forEach((item, idx) => {
        if (y < 60) return;
        page.drawText(`${idx + 1}.`, { x: M, y, size: 9, font: bold, color: BRAND });
        let itemY = y;

        keys.forEach(({ label, field }) => {
          const val = item[field];
          if (!val) return;
          if (itemY < 60) return;
          page.drawText(`   ${label}: ${val}`, { x: M, y: itemY, size: 8, font: regular, color: BLACK, maxWidth: VALUE_MAX + LABEL_W });
          itemY -= 12;
        });

        y = itemY - 6;
      });
    }

    y -= 10;
  }

  // ── Build address ───────────────────────────────────────
  const addressStr = [r.address_line_1, r.address_line_2, r.city, r.state, r.zip].filter(Boolean).join(', ');

  // ── Sections ─────────────────────────────────────────────
  drawSection('Family Information', [
    { label: 'Family Name',   value: r.family_name },
    { label: 'Husband Name',  value: r.husband_name },
    { label: 'Wife Name',     value: r.wife_name },
    { label: 'Address',       value: addressStr },
    { label: 'Phone',         value: r.phone },
    { label: 'Language',      value: r.language },
    { label: 'Status',        value: r.status },
  ]);

  drawListSection('Children', r.children, [
    { label: 'Name',       field: 'name' },
    { label: 'Age',        field: 'age' },
    { label: 'Gender',     field: 'gender' },
    { label: 'Birth Date', field: 'birth_date' },
    { label: 'School Grade', field: 'school_grade' },
  ]);

  drawListSection('Family Members', r.family_members, [
    { label: 'Name',         field: 'name' },
    { label: 'Relationship', field: 'relationship' },
    { label: 'Age',          field: 'age' },
    { label: 'Phone',        field: 'phone' },
  ]);

  // Interests
  if (r.interests && r.interests.length > 0) {
    if (y >= 80) {
      page.drawText('INTERESTS'.toUpperCase(), { x: M, y, size: 9.5, font: bold, color: BRAND });
      y -= 5;
      page.drawLine({ start: { x: M, y }, end: { x: width - M, y }, thickness: 0.4, color: DIVIDER });
      y -= 13;

      const interestsStr = r.interests.join(', ');
      page.drawText(interestsStr, { x: M, y, size: 9, font: regular, color: BLACK, maxWidth: width - M * 2 });
      y -= 20;
    }
  }

  // ── Footer ───────────────────────────────────────────────
  const FY = 30;
  page.drawLine({ start: { x: M, y: FY + 14 }, end: { x: width - M, y: FY + 14 }, thickness: 0.4, color: DIVIDER });
  page.drawText(`${CHURCH.address}  ·  Tel: ${CHURCH.phone}`, {
    x: M, y: FY, size: 7.5, font: regular, color: GRAY,
  });
  page.drawText('Page 1', { x: width - M - 28, y: FY, size: 7.5, font: regular, color: GRAY });

  // ── Download ─────────────────────────────────────────────
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href  = url;
  const safeName = (r.family_name || 'Unknown').replace(/[^a-zA-Z0-9]/g, '_');
  link.download = `Registration_${safeName}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}